import { useMemo } from "react";
import { useStoreId } from "@coworker/app/src/core/auth/useLoggedInUser";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { useTasksByType } from "@coworker/apprestructured/src/task-management/services/useTasksByType";
import { TaskTypeNames } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";
import { Task } from "@coworker/apprestructured/src/task-management/types/Task";
import { QuestionTaskType } from "../BackendApiRepository/QuestionResponse";
import {
  MfaqV2TaskDetails,
  toQuestionTaskType,
} from "../QuestionDetailsPage/QuestionsDetailPage.helper";

interface UseCompletedMfaqV2TasksParams {
  questionId: string | undefined;
  area: string;
  enabled?: boolean;
}

/*
 * Fetches completed v2 MFAQ follow-up tasks from tasks-v2 for a specific
 * {questionId, area} and maps them to the QuestionTaskType so they
 * can be rendered alongside v1 tasks in TaskHistoryComponent.
 */
export function useCompletedMfaqV2Tasks({
  questionId,
  area,
  enabled = true,
}: UseCompletedMfaqV2TasksParams): { completedV2Tasks: QuestionTaskType[] } {
  const storeId = useStoreId();
  const { data: taskTypes } = useAllTaskTypes();

  const mfaqTaskTypeId = useMemo(
    () =>
      taskTypes?.find(({ name }) => name === TaskTypeNames.MFAQ_FOLLOW_UP)?.id,
    [taskTypes]
  );

  const shouldFetch = enabled && !!questionId && !!area;

  const { data: tasks = [] } = useTasksByType(
    shouldFetch ? mfaqTaskTypeId : null,
    shouldFetch ? storeId : null,
    shouldFetch ? "completed" : null
  );

  const completedV2Tasks = useMemo(() => {
    if (!questionId || !area) return [];
    return (tasks as Task[])
      .filter((task) => {
        const details = task.details as MfaqV2TaskDetails;
        return (
          task.state === "completed" &&
          details.question_id === questionId &&
          details.question_area === area
        );
      })
      .map(toQuestionTaskType);
  }, [tasks, questionId, area]);

  return { completedV2Tasks };
}
